// XOBLACK surface strings (see .claude/skills/xoblack-ui/references/recipes.md).
export const CARD =
  'rounded-[36px] border border-neutral-200 bg-white p-5 dark:border-neutral-800 dark:bg-neutral-900'
export const BTN_PRIMARY =
  'inline-flex items-center justify-center gap-2 rounded-[36px] bg-neutral-900 px-6 py-2.5 text-sm font-bold text-white shadow-lg transition-all hover:bg-neutral-800 active:scale-95 disabled:opacity-50 dark:bg-white dark:text-black dark:hover:bg-neutral-200'
export const BTN_SECONDARY =
  'inline-flex items-center justify-center gap-2 rounded-[36px] border border-neutral-200 bg-white px-4 py-2 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-800 dark:bg-transparent dark:text-neutral-300 dark:hover:bg-neutral-800'
export const BTN_DANGER =
  'inline-flex items-center justify-center gap-2 rounded-[36px] bg-red-500 px-4 py-2 text-sm font-bold text-white transition-all hover:bg-red-600 active:scale-95 disabled:opacity-50'
export const INPUT =
  'w-full rounded-[36px] border border-neutral-200 bg-neutral-50 px-4 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white'
export const TEXTAREA =
  'min-h-[120px] w-full rounded-[24px] border border-neutral-200 bg-neutral-50 px-4 py-3 font-mono text-xs text-neutral-900 placeholder:text-neutral-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 dark:border-neutral-800 dark:bg-neutral-900 dark:text-white'
export const LABEL =
  'mb-1.5 block text-xs font-semibold text-neutral-500 dark:text-neutral-400'
export const MUTED = 'text-xs text-neutral-500 dark:text-neutral-400'
export const SKELETON =
  'animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800'
export const PILL =
  'inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-0.5 text-[11px] font-semibold text-neutral-700 dark:border-neutral-800 dark:bg-neutral-800 dark:text-neutral-300'
export const CHIP_ON =
  'rounded-full border border-neutral-900 bg-neutral-900 px-3 py-1 text-xs font-semibold text-white dark:border-white dark:bg-white dark:text-black'
export const CHIP_OFF =
  'rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs font-semibold text-neutral-600 hover:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-400 dark:hover:border-neutral-600'

export const STATUS_PILL: Record<string, string> = {
  queued: PILL,
  running:
    'inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-amber-600 dark:text-amber-400',
  completed:
    'inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400',
  failed:
    'inline-flex items-center gap-1 rounded-full bg-red-500/10 px-2.5 py-0.5 text-[11px] font-semibold text-red-600 dark:text-red-400',
  cancelled: PILL
}

// Section overlay colours: a categorical palette for drawing detected
// sections over the screenshot only. Surfaces keep colour for meaning.
// Class names are written out in full so Tailwind can see them.
export const SECTION_STYLE: Record<
  string,
  { box: string; tag: string; dot: string }
> = {
  about: {
    box: 'border-2 border-indigo-500 bg-indigo-500/10',
    tag: 'bg-indigo-500',
    dot: 'bg-indigo-500'
  },
  contact: {
    box: 'border-2 border-sky-500 bg-sky-500/10',
    tag: 'bg-sky-500',
    dot: 'bg-sky-500'
  },
  hours: {
    box: 'border-2 border-teal-500 bg-teal-500/10',
    tag: 'bg-teal-500',
    dot: 'bg-teal-500'
  },
  menu: {
    box: 'border-2 border-orange-500 bg-orange-500/10',
    tag: 'bg-orange-500',
    dot: 'bg-orange-500'
  },
  products: {
    box: 'border-2 border-purple-500 bg-purple-500/10',
    tag: 'bg-purple-500',
    dot: 'bg-purple-500'
  },
  services: {
    box: 'border-2 border-violet-500 bg-violet-500/10',
    tag: 'bg-violet-500',
    dot: 'bg-violet-500'
  },
  pricing: {
    box: 'border-2 border-yellow-500 bg-yellow-500/10',
    tag: 'bg-yellow-500',
    dot: 'bg-yellow-500'
  },
  events: {
    box: 'border-2 border-pink-500 bg-pink-500/10',
    tag: 'bg-pink-500',
    dot: 'bg-pink-500'
  },
  team: {
    box: 'border-2 border-lime-500 bg-lime-500/10',
    tag: 'bg-lime-500',
    dot: 'bg-lime-500'
  },
  reviews: {
    box: 'border-2 border-rose-500 bg-rose-500/10',
    tag: 'bg-rose-500',
    dot: 'bg-rose-500'
  },
  gallery: {
    box: 'border-2 border-cyan-500 bg-cyan-500/10',
    tag: 'bg-cyan-500',
    dot: 'bg-cyan-500'
  },
  social: {
    box: 'border-2 border-blue-500 bg-blue-500/10',
    tag: 'bg-blue-500',
    dot: 'bg-blue-500'
  },
  booking: {
    box: 'border-2 border-green-500 bg-green-500/10',
    tag: 'bg-green-500',
    dot: 'bg-green-500'
  },
  faq: {
    box: 'border-2 border-neutral-400 bg-neutral-400/10',
    tag: 'bg-neutral-400',
    dot: 'bg-neutral-400'
  },
  navigation: {
    box: 'border-2 border-neutral-400 bg-neutral-400/10',
    tag: 'bg-neutral-400',
    dot: 'bg-neutral-400'
  },
  footer: {
    box: 'border-2 border-neutral-400 bg-neutral-400/10',
    tag: 'bg-neutral-400',
    dot: 'bg-neutral-400'
  },
  other: {
    box: 'border-2 border-neutral-400 bg-neutral-400/10',
    tag: 'bg-neutral-400',
    dot: 'bg-neutral-400'
  }
}
export const SECTION_FALLBACK = SECTION_STYLE.other

export function cls(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ')
}
